import { useCallback, useEffect, useRef, useState } from 'react';

import type { SkillInvocationListItem } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

type Status = 'idle' | 'loading' | 'success' | 'error';

/**
 * ViewModel for paginated per-skill execution history. Used by both the
 * Details-tab preview list (limit=10) and the Execution-History-tab list
 * (limit=25). Cursor-paginated via `starting_after`.
 *
 * `enabled` lets the parent suppress fetches while a different tab is active.
 * Disabling resets the state so re-enabling does a fresh first-page load.
 */
export function useSkillInvocations(
  skillId: string | null,
  options: { limit?: number; enabled?: boolean } = {},
) {
  const apiClient = useApiClient();
  const { limit = 25, enabled = true } = options;
  const [invocations, setInvocations] = useState<SkillInvocationListItem[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  const generationRef = useRef(0);

  useEffect(() => {
    if (!skillId || !enabled) {
      setInvocations([]);
      setStatus('idle');
      setHasMore(false);
      return;
    }
    const gen = ++generationRef.current;
    setStatus('loading');
    setError(undefined);
    apiClient
      .listSkillInvocations(skillId, { limit })
      .then((result) => {
        if (gen !== generationRef.current) return;
        setInvocations(result.data);
        setHasMore(result.has_more);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (gen !== generationRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load invocations');
        setStatus('error');
      });
  }, [apiClient, skillId, limit, enabled]);

  const loadMore = useCallback(() => {
    if (!skillId || !hasMore || invocations.length === 0) return;
    const last = invocations[invocations.length - 1];
    if (!last) return;
    const gen = generationRef.current;
    apiClient
      .listSkillInvocations(skillId, { limit, startingAfter: last.id })
      .then((result) => {
        if (gen !== generationRef.current) return;
        setInvocations((prev) => [...prev, ...result.data]);
        setHasMore(result.has_more);
      })
      .catch((err: unknown) => {
        if (gen !== generationRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load more invocations');
      });
  }, [apiClient, skillId, hasMore, invocations, limit]);

  return { invocations, status, error, hasMore, loadMore };
}
